import { useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ModalCreateBulkWrapper,
  ModalUpdateBulkWrapper,
} from "../../../../../../components/templates/modal/modal";
import { PushToXeroListAction } from "../../../../../../components/molecules/xero/PushToXeroActions";

const CommonActionForMaster = ({ selectedOrderId, orderType }) => {
  const createRef = useRef(null);
  const updateRef = useRef(null);
  const navigate = useNavigate();

  const { titleName, url } = masterData[orderType] || {};

  // Bulk operations are currently not supported for the Site module.
  // We explicitly disable the update button if more than 1 site is selected.
  const isUpdateAllowed = useMemo(() => {
    if (masterDataWithoutBulk.includes(url)) {
      return selectedOrderId?.length === 1;
    }
    return selectedOrderId?.length > 0;
  }, [selectedOrderId, url]);

  if (!masterModule.includes(orderType)) return null;

  const handleCreateClick = () => {
    if (masterDataWithoutBulk.includes(url)) {
      // Bypass the bulk modal and navigate directly to the single create page
      navigate(`/master/${url}/create`);
    } else {
      createRef.current?.setOpenBulkCreateModal(true);
    }
  };

  const handleUpdateClick = () => {
    if (masterDataWithoutBulk.includes(url)) {
      // Bypass the bulk modal and navigate directly to the single update page
      navigate(`/master/${url}/update/${selectedOrderId[0]}`);
    } else {
      updateRef.current?.setOpenBulkUpdateModal(true);
    }
  };

  const getUpdateTooltip = () => {
    if (isUpdateAllowed) return undefined;
    // Provide a specific tooltip for the Site module since bulk update is disabled
    if (masterDataWithoutBulk.includes(url) && selectedOrderId?.length > 1) {
      return `Only one ${titleName} can be updated at a time.`;
    }
    return masterDataWithoutBulk.includes(url)
      ? `Select one ${titleName} to update.`
      : `Select one or more ${titleName} to update.`;
  };

  return (
    <>
      <li>
        <button className="tableButton activeState" onClick={handleCreateClick}>
          Create
        </button>
      </li>
      <ModalCreateBulkWrapper ref={createRef} module={url} />
      <ModalUpdateBulkWrapper ref={updateRef} module={url} selectedIds={selectedOrderId} />

      <li>
        <button
          className={`tableButton ${isUpdateAllowed ? "activeState" : ""}`}
          disabled={!isUpdateAllowed}
          title={getUpdateTooltip()}
          onClick={handleUpdateClick}
        >
          Update
        </button>
      </li>
      <PushToXeroListAction orderType={orderType} selectedOrderId={selectedOrderId} />
    </>
  );
};

export { CommonActionForMaster };

const masterDataWithoutBulk = ["site", "work_center", "people", "charge-type"];

const masterData = {
  warehouse: {
    titleName: "warehouse",
    url: "warehouse",
  },
  supplier: {
    titleName: "supplier",
    url: "supplier",
  },
  product: {
    titleName: "product",
    url: "product",
  },
  productCategory: {
    titleName: "product category",
    url: "product_category",
  },
  productTag: {
    titleName: "product tag",
    url: "product_tag",
  },
  productAlias: {
    titleName: "product alias",
    url: "product_alias",
  },
  pack: {
    titleName: "pack",
    url: "pack",
  },
  forecast: {
    titleName: "forecast",
    url: "forecast",
  },
  warehouseCosting: {
    titleName: "facility costing",
    url: "warehouse_costing",
  },
  stockZone: {
    titleName: "stock zone",
    url: "stock_zone",
  },
  stockLocation: {
    titleName: "stock location",
    url: "stock_location",
  },
  productPlan: {
    titleName: "product plan",
    url: "product_plan",
  },
  transactionReason: {
    titleName: "transaction reason",
    url: "transaction_reason",
  },
  standardCost: {
    titleName: "standard cost",
    url: "standard_costing",
  },
  chartsOfAccount: {
    titleName: "chart of account",
    url: "charts_of_account",
  },
  paymentTerm: {
    titleName: "payment term",
    url: "payment_term",
  },
  billChargeType: {
    titleName: "charge type",
    url: "charge-type",
  },
  taxRate: {
    titleName: "tax rate",
    url: "tax_rate",
  },
  site: {
    titleName: "site",
    url: "site",
  },
  workCenter: {
    titleName: "work centre",
    url: "work_center",
  },
  people: {
    titleName: "people",
    url: "people",
  },
  customer: {
    titleName: "customer",
    url: "customer",
  },
  customerTag: {
    titleName: "customer tag",
    url: "customer_tag",
  },
  taxCategory: {
    titleName: "tax category",
    url: "tax_category",
  },
  productTaxClassification: {
    titleName: "product tax classification",
    url: "product_tax_classification",
  },
  unitOfMeasure: {
    titleName: "unit of measure",
    url: "unit_of_measure",
  },
  equipmentType: {
    titleName: "equipment type",
    url: "equipment_type",
  },
  equipment: {
    titleName: "equipment",
    url: "equipment",
  },
  salesRep: {
    titleName: "sales rep",
    url: "sales_rep",
  },
};

export const masterModule = [
  "product",
  "productCategory",
  "productTag",
  "productAlias",
  "warehouse",
  "supplier",
  "pack",
  "stockZone",
  "stockLocation",
  "forecast",
  "warehouseCosting",
  "productPlan",
  "transactionReason",
  "standardCost",
  "chartsOfAccount",
  "paymentTerm",
  "billChargeType",
  "taxRate",
  "site",
  "workCenter",
  "people",
  "customer",
  "customerTag",
  "taxCategory",
  "productTaxClassification",
  "unitOfMeasure",
  "equipmentType",
  "equipment",
  "salesRep",
];
